/**
 * useDeclareEnumPreview — pre-fetches the value list the declare dialog shows
 * before the user commits.
 *
 * Each time a fresh dialog context lands (open, or a mode switch) the hook
 * re-fetches for that mode's source. Native + CHECK lookups are fast; sampled
 * may take a moment, hence the `loading` flag the dialog renders as a spinner.
 *
 * `lookup` mode additionally carries the FK target + the label column resolved
 * live from `tableDisplayColumns` — the fetch waits for `labelReady` so it
 * fires once with the right label column instead of twice (keys, then pairs).
 */

import { useEffect, useState } from 'react';
import { fetchEnumValues } from '../../services/metaInfoApi';
import { modeToSource, type DeclareEnumDialogError } from './DeclareEnumColumnDialog';
import type { DeclareEnumColumnDialogContext } from './declareEnumColumnDialogStore';

/** Extract a usable message for the error banner; falls back when an `unknown`
 *  rejection has no `.message` (e.g. a non-Error throw). */
export const enumDialogErrorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
};

export type DeclareEnumPreview = {
  values: string[] | undefined;
  /** Aligned with `values`; present only for the `lookup` source. */
  labels: (string | null)[] | undefined;
  truncated: boolean;
  loading: boolean;
  error: DeclareEnumDialogError | null;
};

export function useDeclareEnumPreview(args: {
  isOpen: boolean;
  context: DeclareEnumColumnDialogContext | null;
  /** Lookup mode only — the label column to read names from (`null` = keys only). */
  labelColumn: string | null;
  /** Lookup mode only — false while the label column is still resolving. */
  labelReady: boolean;
}): DeclareEnumPreview {
  const { isOpen, context, labelColumn, labelReady } = args;
  const [values, setValues] = useState<string[] | undefined>(undefined);
  const [labels, setLabels] = useState<(string | null)[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<DeclareEnumDialogError | null>(null);

  useEffect(() => {
    if (!isOpen || !context) {
      setValues(undefined);
      setLabels(undefined);
      setTruncated(false);
      setLoading(false);
      setError(null);
      return;
    }
    const lookupTarget = context.mode === 'lookup' ? context.lookupTarget : undefined;
    setError(null);
    setLoading(true);
    // Hold the spinner until the label column resolves — a keys-only fetch
    // here would flash label-less chips and waste a round trip.
    if (lookupTarget && !labelReady) return;
    let cancelled = false;
    fetchEnumValues(
      context.connectionId,
      context.database,
      context.schema,
      context.table,
      context.column,
      context.engine,
      modeToSource(context.mode),
      context.path,
      context.pathKind,
      lookupTarget
        ? {
            toSchema: lookupTarget.toSchema,
            toTable: lookupTarget.toTable,
            toColumn: lookupTarget.toColumn,
            labelColumn,
          }
        : undefined,
    )
      .then((res) => {
        if (cancelled) return;
        setValues(res.values);
        setLabels(res.labels);
        setTruncated(res.truncated);
      })
      .catch((err) => {
        if (cancelled) return;
        setValues([]);
        setLabels(undefined);
        setError({
          operation: 'sample',
          message: enumDialogErrorMessage(
            err,
            'Failed to sample values. Check your connection and try again.',
          ),
        });
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, context, labelColumn, labelReady]);

  return { values, labels, truncated, loading, error };
}
